import InternalDailyReports from '../../components/dailyReport/InternalDailyReports';
import { dailyText } from '../../components/dailyReport/dailyReportI18n';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  BarChart3,
  CalendarDays,
  Car,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ReceiptText,
  Settings,
  Shield,
  UserRound,
  WalletCards,
} from 'lucide-react';
import InternalLogin from './InternalLogin';
import FleetDashboard from '../../components/fleet/FleetDashboard';
import FleetVehicles from '../../components/fleet/FleetVehicles';
import FleetDrivers from '../../components/fleet/FleetDrivers';
import FleetFinances from '../../components/fleet/FleetFinances';
import FleetPayroll from '../../components/fleet/FleetPayroll';
import FleetDispatchOverview from '../../components/fleet/FleetDispatchOverview';
import FleetSettings from '../../components/fleet/FleetSettings';
import { BACKEND_URL as API } from '../../apiConfig';
import shonanPalmLogo from '../../assets/shonan-palm-gold-transparent.png';
import { getInternalFleetCopy, INTERNAL_LANGUAGE_STORAGE_KEY } from './internalFleetI18n';

// Axios interceptor for internal token
axios.interceptors.request.use(
  (config) => {
    if (config.url?.includes('/api/internal/')) {
      const token = localStorage.getItem('internal_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const InternalDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'vehicles' ? 'vehicles' : 'dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.matchMedia?.('(max-width: 639px)').matches || false);
  const [internalLanguage, setInternalLanguage] = useState(() => (
    localStorage.getItem(INTERNAL_LANGUAGE_STORAGE_KEY) || 'zh'
  ));
  const t = getInternalFleetCopy(internalLanguage);

  const handleLanguageChange = (language) => {
    setInternalLanguage(language);
    localStorage.setItem(INTERNAL_LANGUAGE_STORAGE_KEY, language);
  };

  // 检查用户是否有指定权限
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // 管理员拥有所有权限
    return user.permissions?.includes(permission);
  };

  // 根据权限过滤导航菜单 - 使用useMemo在user变化时重新计算
  const tabs = React.useMemo(() => {
    const allTabs = [
      { id: 'dashboard', label: t.nav.dashboard, icon: BarChart3, permission: null }, // 所有人可见
      { id: 'vehicles', label: t.nav.vehicles, icon: Car, permission: 'vehicles_view' },
      { id: 'daily-reports', label: dailyText(internalLanguage).manage, icon: ReceiptText, permission: 'drivers_view' },
      { id: 'drivers', label: t.nav.drivers, icon: UserRound, permission: 'drivers_view' },
      { id: 'dispatch', label: t.nav.dispatch, icon: CalendarDays, permission: null },
      { id: 'finances', label: t.nav.finances, icon: WalletCards, permission: 'finances_view' },
      { id: 'payroll', label: t.nav.payroll, icon: ReceiptText, permission: null },
      { id: 'settings', label: t.nav.settings, icon: Settings, permission: null }, // 所有人可见（内部会限制功能）
    ];
    
    if (!user) return allTabs.filter(t => !t.permission); // 未登录只显示无权限要求的
    if (user.role === 'admin') return allTabs; // 管理员显示全部
    
    return allTabs.filter(tab => {
      if (!tab.permission) return true;
      return user.permissions?.includes(tab.permission);
    });
  }, [user, t, internalLanguage]);

  useEffect(() => {
    checkAuth();
    initAdmin();
    // Internal auth is checked once when the internal dashboard mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initAdmin = async () => {
    // 初始化管理员账号（如果不存在）
    try {
      await axios.post(`${API}/api/internal/auth/init-admin`);
    } catch (err) {
      // 忽略错误（可能已存在）
    }
  };

  const tryAdminSso = async () => {
    const adminToken = localStorage.getItem('token');
    if (!adminToken) return false;

    try {
      const res = await axios.post(`${API}/api/internal/auth/admin-sso`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      localStorage.setItem('internal_token', res.data.internal_token);
      localStorage.setItem('internal_user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      return true;
    } catch (err) {
      return false;
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('internal_token');
    const savedUser = localStorage.getItem('internal_user');
    let authenticated = false;
    
    if (token && savedUser) {
      try {
        // 验证token
        const res = await axios.get(`${API}/api/internal/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
        authenticated = true;
      } catch (err) {
        // Token无效，清除
        localStorage.removeItem('internal_token');
        localStorage.removeItem('internal_user');
      }
    }
    if (!authenticated) {
      await tryAdminSso();
    }
    setLoading(false);
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('internal_token');
    localStorage.removeItem('internal_user');
    setUser(null);
  };

  const handleNavigate = (tab, options = {}) => {
    setActiveTab(tab);
    // 传递action给子组件（如打开添加弹窗）
    if (options.action === 'add') {
      setTimeout(() => {
        // 触发对应页面的添加按钮
        const addBtn = document.querySelector(`[data-testid="add-${tab === 'vehicles' ? 'vehicle' : 'driver'}-btn"]`);
        if (addBtn) addBtn.click();
      }, 100);
    }
  };

  const renderContent = () => {
    // 权限检查 - 如果用户没有权限访问当前tab，显示无权限提示
    const checkTabPermission = (tabId, permission) => {
      if (!permission) return true;
      return hasPermission(permission);
    };

    const tabPermissions = {
      'vehicles': 'vehicles_view',
      'drivers': 'drivers_view',
      'daily-reports': 'drivers_view', 
      'finances': 'finances_view',
    };

    if (tabPermissions[activeTab] && !checkTabPermission(activeTab, tabPermissions[activeTab])) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <span className="text-6xl mb-4">🔒</span>
          <p className="text-xl text-foreground">{t.noPermissionTitle}</p>
          <p className="text-sm mt-2">{t.noPermissionHint}</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <FleetDashboard onNavigate={handleNavigate} user={user} hasPermission={hasPermission} language={internalLanguage} />;
      case 'vehicles':
        return <FleetVehicles canEdit={hasPermission('vehicles_edit')} language={internalLanguage} />;
      case 'daily-reports':
        return <InternalDailyReports language={internalLanguage} canEdit={hasPermission('drivers_edit')} />;
      case 'drivers':
        return <FleetDrivers canEdit={hasPermission('drivers_edit')} language={internalLanguage} />;
      case 'dispatch':
        return <FleetDispatchOverview language={internalLanguage} />;
      case 'finances':
        return <FleetFinances canEdit={hasPermission('finances_edit')} language={internalLanguage} />;
      case 'payroll':
        return <FleetPayroll language={internalLanguage} onLanguageChange={handleLanguageChange} />;
      case 'settings':
        return <FleetSettings user={user} language={internalLanguage} />;
      default:
        return <FleetDashboard user={user} hasPermission={hasPermission} language={internalLanguage} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen premium-shell flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <InternalLogin onLogin={handleLogin} />;
  }

  return (
    <div className="internal-alloy-shell flex">
      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-20' : 'w-72'} internal-alloy-sidebar transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="internal-alloy-logo-card p-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="shonan-brand-logo-unframed flex h-11 w-11 shrink-0 items-center justify-center">
              <img
                src={shonanPalmLogo}
                alt="Shonan International"
                className="h-8 w-8 object-contain drop-shadow-[0_0_18px_rgba(216,183,105,0.52)]"
              />
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0 max-w-[178px]">
                <h1 className="whitespace-nowrap font-serif text-[10px] font-semibold tracking-[0.09em] text-[#d7b76b]">
                  SHONAN INTERNATIONAL
                </h1>
                <p className="mt-1 whitespace-nowrap text-[8px] font-semibold tracking-[0.1em] text-white/58">
                  Private Transfer & Charter
                </p>
                <p className="mt-1 truncate text-[10px] font-semibold tracking-[0.12em] text-[#d8b769]/78">
                  {t.productName}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {tabs.map((tab) => (
            <NavButton
              key={tab.id}
              tab={tab}
              active={activeTab === tab.id}
              collapsed={sidebarCollapsed}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </nav>

        {/* Collapse Button & Logout */}
        <div className="space-y-2 border-t border-[#d8b769]/16 p-4">
          {user.role === 'admin' && (
            <a
              href="/"
              data-testid="internal-admin-return-link"
              className="internal-alloy-action w-full whitespace-nowrap"
            >
              <ArrowLeft className="h-4 w-4 shrink-0" />
              {!sidebarCollapsed && <span className="truncate">{t.adminReturn}</span>}
            </a>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="internal-alloy-action w-full whitespace-nowrap"
            aria-label={sidebarCollapsed ? t.expandMenu : t.collapseMenu}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronLeft className="h-4 w-4 shrink-0" />}
            {!sidebarCollapsed && <span className="truncate">{t.collapseMenu}</span>}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="internal-alloy-action w-full whitespace-nowrap !text-red-300 hover:!border-red-400/30 hover:!bg-red-500/10 hover:!text-red-200"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!sidebarCollapsed && <span className="truncate">{t.logout}</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <header className="internal-alloy-header px-6 py-4">
          <div className="flex min-w-0 items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="truncate text-xl font-bold text-white">
                {tabs.find(t => t.id === activeTab)?.label}
              </h2>
              <p className="truncate text-sm text-slate-400">
                {t.companySystem}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-4">
              <label className="hidden items-center gap-2 rounded-xl border border-[#d8b769]/25 bg-black/30 px-3 py-2 text-xs text-slate-300 sm:flex">
                <span>{t.languageLabel}</span>
                <select
                  value={internalLanguage}
                  onChange={(event) => handleLanguageChange(event.target.value)}
                  className="border-0 bg-transparent text-[#f3d98b] outline-none"
                  data-testid="internal-global-language-select"
                >
                  <option value="zh">{t.chinese}</option>
                  <option value="ja">{t.japanese}</option>
                </select>
              </label>
              <div className="hidden text-right sm:block">
                <p className="max-w-40 truncate text-sm font-medium text-white">{user.name}</p>
                <p className="text-xs text-slate-400">
                  {user.role === 'admin' ? t.admin : t.operator}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d8b769]/35 bg-[#d8b769]/12 text-[#f3d98b] shadow-sm shadow-[#d8b769]/10">
                <Shield className="h-5 w-5" />
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="internal-alloy-content flex-1 overflow-auto p-4 sm:p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

function NavButton({ tab, active, collapsed, onClick }) {
  const Icon = tab.icon;

  return (
    <button
      onClick={onClick}
      data-testid={`nav-${tab.id}`}
      title={collapsed ? tab.label : undefined}
      className={`internal-alloy-nav-item ${
        active ? 'internal-alloy-nav-item-active' : 'internal-alloy-nav-item-idle'
      } ${collapsed ? 'justify-center px-0' : ''}`}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span className="min-w-0 truncate whitespace-nowrap">{tab.label}</span>}
    </button>
  );
}

export default InternalDashboard;
